import {
  findEmployeesWithFields,
  inviteEmployees,
} from "../../infrastructure/employees-cache";
import { findCompanyId } from "../../infrastructure/repository/company-repository";
import { displayUploadEmployeesPage } from "./displays/display-upload-employees-page";
import { showSuccessMessage } from "../commons/show-success-message";
import { showErrorMessage } from "../commons/show-error-message";
import { logError, addContextLog } from "../../infrastructure/log";
import { translate as t } from "../../infrastructure/translate/localization";
import { deliverBagToEmployees } from "../benefit-plan/deliver-bag-from-existant-in-model";
import { findModels } from "../../infrastructure/repository/benefit-models-repository";
import adaptEmployeesForInvite from "../../infrastructure/adapters/adaptEmployeesForInvite";
import { sendInviteEmployees } from "../../infrastructure/repository/employees-repository";

export async function inviteUploadingEmployees(employeesIds) {
  try {
    const companyId = await findCompanyId();
    const allEmployees = await findEmployeesWithFields({ companyId });
    const notRemovedEmployees = allEmployees.filter(
      (e) => e.state !== "removed"
    );
    const currentEmployees = notRemovedEmployees.filter(
      (e) => e.state === "uploading"
    );
    const selectedEmployees = employeesIds.map((employeeId) =>
      currentEmployees.find(({ id }) => id === employeeId)
    );
    const { data: models } = await findModels();
    const targetModel =
      models.find(({ id }) => selectedEmployees?.[0].modelId === id) || {};
    const allEmployeesDontGoToSameModel = selectedEmployees.some(
      ({ modelId }) => modelId !== targetModel.id
    );

    if (allEmployeesDontGoToSameModel) {
      showErrorMessage(t("employees-section.error-employees-invite"));
    } else {
      const benefitBagConfig = targetModel?.benefitBagConfig || [];
      const activeBags = benefitBagConfig.filter(
        ({ state }) => state === "active"
      );
      const modelHasAnActiveBag = activeBags.length > 0;

      const inviteAction = async () => {
        try {
          await inviteEmployees({
            employees: currentEmployees
              .map((e) => ({
                id: e.id,
                name: e.name,
                surname: e.surname,
                legalId: e.legalId,
                modelId: e.modelId,
                email: e.email,
                state: e.state,
                grossSalary: e.grossSalary,
              }))
              .filter((e) => employeesIds.includes(e.id)),
            companyId,
          });
          showSuccessMessage(t("employees-section.invited-employee-success"));
        } catch (err) {
          showErrorMessage(t("common-messages.basic-error-message"), true);
        }
      };

      if (modelHasAnActiveBag) {
        deliverBagToEmployees({
          targetModel,
          employees: currentEmployees,
          activeBags,
          onConfirm: inviteAction,
          navigateWhenDoneTo: displayUploadEmployeesPage,
        });
      } else {
        await inviteAction();
      }

      addContextLog("info", "inviteUploadingEmployees", "request");

      displayUploadEmployeesPage();
    }
  } catch (e) {
    logError(e, `Error to inviteUploadingEmployees`);
    showErrorMessage(t("common-messages.basic-error-message"), true);
  }
}

export const inviteUploadingEmployeesV2 = async ({
  company,
  employees,
  benefitPlan,
  addEmployeesToBenefitPlanWithBag,
  onInviteSuccessfully,
}) => {
  const hasABagWithFunds = parseFloat(benefitPlan.benefitBag.amount) > 0;

  const inviteAction = async () => {
    try {
      const res = await sendInviteEmployees({
        employees: adaptEmployeesForInvite({ employees }),
        companyId: company.id,
      });
      if (res.data.length > 0) {
        showSuccessMessage(
          t("success-messages.employees-invited-successfully")
        );
        if (res.data.length < employees.length) {
          showErrorMessage(
            t("upload-employees-section.some-employees-could-not-be-invited")
          );
        }
        if (onInviteSuccessfully) {
          await onInviteSuccessfully();
        }
      } else {
        showErrorMessage(
          t("upload-employees-section.could-not-invite-the-employees")
        );
      }

      return res.data;
    } catch (e) {
      showErrorMessage(t("common-messages.basic-error-message"));
      return [];
    }
  };

  if (hasABagWithFunds) {
    addEmployeesToBenefitPlanWithBag({
      inviteEmployeesHandler: inviteAction,
    });
  } else {
    await inviteAction();
  }
};
